import {
  ActionIcon,
  Box,
  Center,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { deleteCategoryById, getCategories } from "api/category.service";
import { safeApiCall } from "api/common.service";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import CategoryModal from "components/products/CategoryModal";
import React, { useEffect, useMemo, useState } from "react";
import { Categories, Category } from "types/model.types";

const ManageCategoryPage: React.FC = () => {
  const [categories, setCategories] = useState<Categories>([]);
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [page, setPage] = useState(1);
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredItems = useMemo(() => {
    return categories.filter((b) =>
      b.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categories, searchTerm]);

  const paginatedItems = useMemo(() => {
    const start = (activePage - 1) * DEFAULT_PAGE_SIZE;
    return filteredItems.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredItems, activePage]);

  // Fetch brands when the component mounts
  useEffect(() => {
    (async () => {
      try {
        const response = await safeApiCall(getCategories());
        setCategories(response?.data ?? []);
      } catch (error) {
        console.error("Failed to fetch categries:", error);
      }
    })();
  }, [reloadTrigger]);

  const rows = paginatedItems.map((item: Category) => (
    <Table.Tr key={item.id}>
      <Table.Td>{item.name}</Table.Td>
      <Table.Td>{item.total}</Table.Td>
      <Table.Td>
        <ActionIcon
          onClick={() => {
            setEditCategory(item);
            setModalOpened(true);
          }}
        >
          <IconPencil size={18} />
        </ActionIcon>
      </Table.Td>
      <Table.Td>
        <ActionIcon
          color="red"
          onClick={async () => {
            await safeApiCall(deleteCategoryById(item.id));
            setCategories((prev) => prev.filter((b) => b.id !== item.id));
          }}
        >
          <IconTrash size={18} />
        </ActionIcon>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearchTerm(value);
  };

  return (
    <Box>
      <Stack>
        <Title order={2}>Manage Product Categories</Title>
        <Text size="sm" c="dimmed">
          Showing 30 items per page
        </Text>
        <TextInput
          placeholder="Search by any title"
          mb="md"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Table
          striped
          highlightOnHover
          withTableBorder
          style={{ minWidth: 740 }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th colSpan={4}>
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Text>Add New Category</Text>
                  <ActionIcon
                    variant="filled"
                    color="blue"
                    onClick={() => {
                      setEditCategory({ id: 0, name: "", total: 0 });
                      setModalOpened(true);
                    }}
                  >
                    <IconPlus size={16} />
                  </ActionIcon>
                </Box>
              </Table.Th>
            </Table.Tr>
            <Table.Tr>
              <Table.Th style={{ width: "75%" }}>Title</Table.Th>
              <Table.Th>Total</Table.Th>
              <Table.Th>Edit</Table.Th>
              <Table.Th>Delete</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={7}>
                <Center>
                  <Pagination
                    total={Math.ceil(paginatedItems.length / DEFAULT_PAGE_SIZE)}
                    value={activePage}
                    onChange={setActivePage}
                    mt="md"
                  />
                </Center>
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
        <CategoryModal
          opened={modalOpened}
          onClose={() => {
            setModalOpened(false);
            setEditCategory(null);
          }}
          forceReload={() => {
            setReloadTrigger(new Date());
          }}
          category={editCategory}
          existingCategories={categories}
          setCategories={setCategories}
        />
      </Stack>
    </Box>
  );
};

export default ManageCategoryPage;
